import {
  BarChart,
  Bar,
  Brush,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  Tooltip,
  LabelList,
  ResponsiveContainer,
} from "recharts";

import { useMediaQuery } from "react-responsive";
import { ChartWrapper } from "../../components/ChartWrapper";
import { formatNumber } from "../../../utils/number";
import { formatLabel } from "../../../utils/string";

/* -------- TOTAL -------- */
const getTotal = (data = []) =>
  data.reduce(
    (sum, item) =>
      sum +
      (item?.Easy || 0) +
      (item?.Intermediate || 0) +
      (item?.Difficult || 0),
    0
  );

/* -------- SMART LABEL -------- */
const renderLabel = (isSmall) => (props) => {
  const { x, y, width, height, value } = props;

  if (!value) return null;

  // small bars → above
  if (height < 18) {
    return (
      <text
        x={x + width / 2}
        y={Math.max(y - 4, 12)}
        textAnchor="middle"
        fill="#111"
        fontSize={isSmall ? 9 : 11}
        fontWeight="600"
      >
        {formatNumber(value)}
      </text>
    );
  }

  return (
    <text
      x={x + width / 2}
      y={y + height / 2}
      textAnchor="middle"
      dominantBaseline="middle"
      fill="#fff"
      fontSize={isSmall ? 9 : 11}
      fontWeight="700"
    >
      {formatNumber(value)}
    </text>
  );
};

export default function DifficultyReport({ data = [], loading = false }) {
  const isSmall = useMediaQuery({ query: "(max-width: 600px)" });

  const isEmpty = !data || data.length === 0;
  const total = getTotal(data);

  return (
    <ChartWrapper
      loading={loading}
      isEmpty={isEmpty}
      title="Question Difficulty Analysis"
      description={`Distribution of easy, intermediate, and difficult questions (Total: ${formatNumber(
        total
      )})`}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          barCategoryGap={data.length === 1 ? "35%":isSmall ? "15%" : "20%"}
          barGap={0}
          margin={{
            top: 45,
            right: 20,
            left: isSmall ? 0 : 10,
            bottom: isSmall ? 40 : 60,
          }}
        >
          {/* GRID */}
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />

          {/* X AXIS */}
          <XAxis
            dataKey="Subject"
            tickFormatter={(v) => formatLabel(v)}
            interval={0}
            tick={{
              fontSize: isSmall ? 11 : 13,
              fill: "#222",
              fontWeight: 600,
            }}
            axisLine={{ stroke: "#999", strokeWidth: 1.2 }}
            tickLine={false}
            angle={isSmall ? -25 : 0}
            textAnchor={isSmall ? "end" : "middle"}
          />

          {/* 🔥 SHOW ONLY 3 ITEMS INITIALLY */}
          <Brush
            dataKey="Subject"
            height={10}
            travellerWidth={8}
            stroke="#94a3b8"
            endIndex={Math.min(data.length - 1, 2)} // ✅ only 3 bars
          />

          {/* Y AXIS */}
          <YAxis
            tickFormatter={(v) => formatNumber(v)}
            tick={{
              fontSize: 12,
              fill: "#222",
              fontWeight: 600,
            }}
            axisLine={{ stroke: "#999", strokeWidth: 1.2 }}
            tickLine={false}
            width={45}
            label={{
              value: "Questions",
              angle: -90,
              position: "insideLeft",
              dx: isSmall ? -5 : -10,
              style: {
                textAnchor: "middle",
                fontSize: 12,
                fontWeight: 700,
                fill: "#111",
              },
            }}
          />

          {/* TOOLTIP */}
          <Tooltip
            contentStyle={{
              borderRadius: "10px",
              border: "none",
              boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
              fontSize: "13px",
            }}
          />

          {/* 🔥 GROUPED BARS */}
          <Bar dataKey="Easy" fill="#22c55e" maxBarSize={50}>
            <LabelList content={renderLabel(isSmall)} />
          </Bar>

          <Bar dataKey="Intermediate" fill="#f59e0b" maxBarSize={50}>
            <LabelList content={renderLabel(isSmall)} />
          </Bar>

          <Bar dataKey="Difficult" fill="#ef4444" maxBarSize={50}>
            <LabelList content={renderLabel(isSmall)} />
          </Bar>

          {/* LEGEND */}
          <Legend
            wrapperStyle={{
              fontSize: "14px",
              fontWeight: 600,
              paddingTop: 10,
            }}
          />
        </BarChart>
      </ResponsiveContainer>
    </ChartWrapper>
  );
}